import { bradleyExtensionFromFileName } from '../analytics/bradley-analytics.telemetry.js'

/** Types shown in the upload disclaimer (prototype order). */
export const BRADLEY_DISCLAIMER_FILE_TYPES = ['PNG', 'JPG', 'PDF', 'DOCX', 'CSV', 'PPTX', 'XLSX', 'XLS']

export const BRADLEY_IMAGE_EXTENSIONS = ['.png', '.jpg', '.jpeg']
export const BRADLEY_DOCUMENT_EXTENSIONS = ['.pdf', '.docx', '.csv', '.pptx', '.xlsx', '.xls']
export const BRADLEY_ALLOWED_EXTENSIONS = [...BRADLEY_IMAGE_EXTENSIONS, ...BRADLEY_DOCUMENT_EXTENSIONS]

export const BRADLEY_LEGACY_IMAGE_EXTENSIONS = []
export const BRADLEY_LEGACY_DOCUMENT_EXTENSIONS = ['.docx', '.pptx', '.txt', '.md', '.pdf', '.xlsx', '.xls']
export const BRADLEY_LEGACY_ALLOWED_EXTENSIONS = [...BRADLEY_LEGACY_DOCUMENT_EXTENSIONS]
export const BRADLEY_LEGACY_DISCLAIMER_FILE_TYPES = ['DOCX', 'PPTX', 'TXT', 'MD', 'PDF', 'XLSX', 'XLS']

export const BRADLEY_MAX_FILES_PER_MESSAGE = 10
export const BRADLEY_MAX_FILE_SIZE_MB = 100
export const BRADLEY_MAX_FILE_SIZE_BYTES = BRADLEY_MAX_FILE_SIZE_MB * 1024 * 1024
export const BRADLEY_LEGACY_MAX_FILE_SIZE_MB = 200
export const BRADLEY_LEGACY_MAX_FILE_SIZE_BYTES = BRADLEY_LEGACY_MAX_FILE_SIZE_MB * 1024 * 1024

export const BRADLEY_ATTACHMENT_STATUS = {
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
}

export const BRADLEY_IMAGE_MIME_TYPES = ['image/png', 'image/jpeg']
export const BRADLEY_LEGACY_IMAGE_MIME_TYPES = []

const MIME_TO_EXTENSION = {
  'image/png': '.png',
  'image/jpeg': '.jpg',
  'image/gif': '.gif',
  'image/webp': '.webp',
}

export function getBradleyAttachmentPolicy(newFileUpload = false) {
  if (newFileUpload) {
    return {
      newFileUpload: true,
      imageExtensions: BRADLEY_IMAGE_EXTENSIONS,
      documentExtensions: BRADLEY_DOCUMENT_EXTENSIONS,
      allowedExtensions: BRADLEY_ALLOWED_EXTENSIONS,
      imageMimeTypes: BRADLEY_IMAGE_MIME_TYPES,
      maxFiles: BRADLEY_MAX_FILES_PER_MESSAGE,
      maxFileSizeMb: BRADLEY_MAX_FILE_SIZE_MB,
      maxFileSizeBytes: BRADLEY_MAX_FILE_SIZE_BYTES,
      disclaimerTypes: BRADLEY_DISCLAIMER_FILE_TYPES,
    }
  }

  return {
    newFileUpload: false,
    imageExtensions: BRADLEY_LEGACY_IMAGE_EXTENSIONS,
    documentExtensions: BRADLEY_LEGACY_DOCUMENT_EXTENSIONS,
    allowedExtensions: BRADLEY_LEGACY_ALLOWED_EXTENSIONS,
    imageMimeTypes: BRADLEY_LEGACY_IMAGE_MIME_TYPES,
    maxFiles: BRADLEY_MAX_FILES_PER_MESSAGE,
    maxFileSizeMb: BRADLEY_LEGACY_MAX_FILE_SIZE_MB,
    maxFileSizeBytes: BRADLEY_LEGACY_MAX_FILE_SIZE_BYTES,
    disclaimerTypes: BRADLEY_LEGACY_DISCLAIMER_FILE_TYPES,
  }
}

export function bradleyDisclaimerTypesLabel(newFileUpload = false) {
  return getBradleyAttachmentPolicy(newFileUpload).disclaimerTypes.join(', ')
}

export function createBradleyAttachmentClientId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(8)
    crypto.getRandomValues(bytes)
    const suffix = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('')
    return `bradley-attachment-${Date.now()}-${suffix}`
  }

  return `bradley-attachment-${Date.now()}`
}

function normalizeExtension(extension) {
  return extension?.startsWith('.') ? extension.toLowerCase() : `.${(extension || '').toLowerCase()}`
}

export function isBradleyImageExtension(extension, newFileUpload = false) {
  const policy = getBradleyAttachmentPolicy(newFileUpload)
  return policy.imageExtensions.includes(normalizeExtension(extension))
}

export function isBradleyDocumentExtension(extension, newFileUpload = false) {
  const policy = getBradleyAttachmentPolicy(newFileUpload)
  return policy.documentExtensions.includes(normalizeExtension(extension))
}

export function isBradleyDocumentFile(file, newFileUpload = false) {
  if (!file) {
    return false
  }

  const policy = getBradleyAttachmentPolicy(newFileUpload)
  const fileName = file.name?.toLowerCase?.() || ''
  return policy.documentExtensions.some(extension => fileName.endsWith(extension))
}

export function isBradleyImageFile(file, newFileUpload = false) {
  if (!file) {
    return false
  }

  const policy = getBradleyAttachmentPolicy(newFileUpload)
  const mime = (file.type || '').toLowerCase()
  if (policy.imageMimeTypes.includes(mime)) {
    return true
  }

  const fileName = file.name?.toLowerCase?.() || ''
  return policy.imageExtensions.some(extension => fileName.endsWith(extension))
}

export function isBradleyImageAttachment(item, newFileUpload = false) {
  if (!item || typeof item !== 'object') {
    return false
  }

  if (item.is_image === true) {
    return true
  }

  const extension = bradleyExtensionFromFileName(item.file_name || item.name)
  return isBradleyImageExtension(extension, newFileUpload)
}

export function getBradleyAttachmentStatus(item) {
  const status = item?.upload_status || item?.uploadStatus
  if (status === BRADLEY_ATTACHMENT_STATUS.UPLOADING
    || status === BRADLEY_ATTACHMENT_STATUS.SUCCESS
    || status === BRADLEY_ATTACHMENT_STATUS.ERROR) {
    return status
  }

  if (item?.file_hash || item?.file_id || item?.document_id || item?.id) {
    return BRADLEY_ATTACHMENT_STATUS.SUCCESS
  }

  return BRADLEY_ATTACHMENT_STATUS.SUCCESS
}

export function isBradleyAttachmentReadyForSend(item) {
  if (!item || typeof item !== 'object') {
    return false
  }

  if (getBradleyAttachmentStatus(item) !== BRADLEY_ATTACHMENT_STATUS.SUCCESS) {
    return false
  }

  if (item.transformation_status === 'ready') {
    return Boolean(item.file_hash)
  }

  if (item.transformation_status === 'not_started'
    || item.transformation_status === 'requested'
    || item.transformation_status === 'failed') {
    return false
  }

  return Boolean(item.file_hash || item.file_id || item.document_id || item.id)
}

export function getBradleyAttachmentFileHashForSend(item) {
  if (!item || typeof item !== 'object') {
    return null
  }

  return item.file_hash || null
}

export function areAllBradleyAttachmentsReadyForSend(items) {
  if (!Array.isArray(items) || items.length === 0) {
    return true
  }

  return items.every(isBradleyAttachmentReadyForSend)
}

export function resolveBradleyImageFileName(file, fallbackPrefix = 'pasted-image') {
  const currentName = typeof file?.name === 'string' ? file.name.trim() : ''
  if (currentName && currentName !== 'image.png') {
    return currentName
  }

  const extension = MIME_TO_EXTENSION[(file?.type || '').toLowerCase()] || '.png'
  return `${fallbackPrefix}-${Date.now()}${extension}`
}

export function getBradleyAttachmentSizeBytes(item) {
  if (!item || typeof item !== 'object') {
    return 0
  }

  const candidates = [item.size, item.file_size, item.file_size_bytes, item.size_bytes]
  for (const candidate of candidates) {
    const value = Number(candidate)
    if (Number.isFinite(value) && value >= 0) {
      return value
    }
  }

  return 0
}

export function sumBradleyAttachmentSizes(items) {
  return (Array.isArray(items) ? items : []).reduce(
    (total, item) => total + getBradleyAttachmentSizeBytes(item),
    0,
  )
}

export function formatBradleyFileSize(bytes = 0) {
  const value = Number(bytes)
  if (!Number.isFinite(value) || value <= 0) {
    return '0 MB'
  }

  const megabytes = value / (1024 * 1024)
  if (megabytes >= 10) {
    return `${Math.round(megabytes)} MB`
  }

  if (megabytes >= 1) {
    const rounded = Math.round(megabytes * 10) / 10
    return `${String(rounded).replace(/\.0$/, '')} MB`
  }

  const kilobytes = Math.max(1, Math.round(value / 1024))
  return `${kilobytes} KB`
}

export function validateBradleyAttachment(file, newFileUpload = false) {
  if (!file) {
    return { valid: false, error: 'No file selected', errorType: 'unknown' }
  }

  const policy = getBradleyAttachmentPolicy(newFileUpload)

  if (file.size > policy.maxFileSizeBytes) {
    return {
      valid: false,
      error: `File size exceeds ${policy.maxFileSizeMb}MB limit`,
      errorType: 'file_size_limit',
    }
  }

  if (isBradleyImageFile(file, newFileUpload)) {
    return { valid: true, attachmentType: 'image' }
  }

  if (isBradleyDocumentFile(file, newFileUpload)) {
    return { valid: true, attachmentType: 'document' }
  }

  return {
    valid: false,
    error: `Invalid file type. Allowed: ${policy.allowedExtensions.join(', ').toUpperCase()}`,
    errorType: 'unsupported_format',
  }
}

export function countBradleyImageAttachments(items, newFileUpload = false) {
  return (Array.isArray(items) ? items : []).filter(item => isBradleyImageAttachment(item, newFileUpload)).length
}

export function countBradleyActiveAttachments(items) {
  return (Array.isArray(items) ? items : []).filter((item) => {
    const status = getBradleyAttachmentStatus(item)
    return status === BRADLEY_ATTACHMENT_STATUS.UPLOADING || status === BRADLEY_ATTACHMENT_STATUS.SUCCESS
  }).length
}

export function bradleyAttachmentAcceptAttribute(newFileUpload = false) {
  return getBradleyAttachmentPolicy(newFileUpload).allowedExtensions.join(',')
}

export function buildBradleyLocalAttachment(file, {
  clientId = createBradleyAttachmentClientId(),
  uploadStatus = BRADLEY_ATTACHMENT_STATUS.UPLOADING,
  newFileUpload = false,
} = {}) {
  const isImage = isBradleyImageFile(file, newFileUpload)
  return {
    client_id: clientId,
    file_name: file?.name || 'upload',
    size: file?.size || 0,
    file_size: file?.size || 0,
    is_image: isImage,
    preview_url: isImage && file ? URL.createObjectURL(file) : null,
    upload_status: uploadStatus,
  }
}
